// A friend's or access code a SIGNED-OUT visitor typed into the Subscribe page's code
// field (#6984), kept on the device so it can be filled back into the field after
// they sign in. It is never claimed or redeemed by itself: the person still presses
// Apply, and an access code still asks for confirmation.
//
// Kept for 1 hour only, not the 7 days a sale code gets: an access code is a bearer
// credential until redeemed, and this may be a shared computer.
// See design-docs/subscribe-code-field.md, "Security". No Angular here; the thin
// pending-typed-code.service.ts sibling supplies the storage, the clock and the reporting.
import { DateTime } from 'luxon';
import { normaliseTypedCode } from '@cf-shared/data-model/typed-code';

/** The storage key. `localStorage`, so the code survives the sign-up email opening a new tab. */
export const PENDING_TYPED_CODE_STORAGE_KEY = 'cs_pending_typed_code';

/** How long a kept code is filled back in, in hours. */
export const PENDING_TYPED_CODE_MAX_AGE_HOURS = 1;

/** A minimal view of `localStorage`, so these rules can be tested with a plain object. */
export interface PendingTypedCodeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** What the store could not do. The values are the condition names the service reports under. */
export enum PendingTypedCodeProblem {
  /** The storage refused the write (Safari private mode does this). */
  NotStored = 'PendingTypedCodeNotStored',
  /** The storage refused the read. */
  Unreadable = 'PendingTypedCodeUnreadable',
  /** The stored value was not readable, and was cleared. */
  DamagedCleared = 'PendingTypedCodeDamagedCleared',
}

export type PendingTypedCodeProblemReporter = (problem: PendingTypedCodeProblem, err: unknown) => void;

interface StoredTypedCode {
  code: string;
  storedAt_utc: string;
}

/** Keep a typed code. A blank one is ignored; a storage failure only means the person types it again. */
export function rememberTypedCode(storage: PendingTypedCodeStorage, input: string, now_utc: string, onProblem: PendingTypedCodeProblemReporter): void {
  const code = normaliseTypedCode(input);
  if (!code) return;
  try {
    const payload: StoredTypedCode = { code, storedAt_utc: now_utc };
    storage.setItem(PENDING_TYPED_CODE_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    onProblem(PendingTypedCodeProblem.NotStored, err);
  }
}

/** Forget the kept code. Never throws: a value left behind is cleared again on the next read. */
export function clearTypedCode(storage: PendingTypedCodeStorage): void {
  try {
    storage.removeItem(PENDING_TYPED_CODE_STORAGE_KEY);
  } catch {
    // not reported: the next read clears or ignores it again, and nothing depends on it being gone now
  }
}

/**
 * Take the kept code: answer it and remove it, so it is filled in once. A damaged
 * or too-old value is removed and answers undefined.
 */
export function takeTypedCode(storage: PendingTypedCodeStorage, now_utc: string, onProblem: PendingTypedCodeProblemReporter): string | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(PENDING_TYPED_CODE_STORAGE_KEY);
  } catch (err) {
    onProblem(PendingTypedCodeProblem.Unreadable, err);
    return undefined;
  }
  if (!raw) return undefined;
  clearTypedCode(storage);

  let parsed: StoredTypedCode | undefined;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    onProblem(PendingTypedCodeProblem.DamagedCleared, err);
    return undefined;
  }
  const code = typeof parsed?.code === 'string' ? normaliseTypedCode(parsed.code) : '';
  const storedAt = typeof parsed?.storedAt_utc === 'string' ? DateTime.fromISO(parsed.storedAt_utc, { zone: 'utc' }) : undefined;
  const now = DateTime.fromISO(now_utc, { zone: 'utc' });
  if (!code || !storedAt?.isValid || !now.isValid) {
    onProblem(PendingTypedCodeProblem.DamagedCleared, new Error('stored typed code has no code or no valid time'));
    return undefined;
  }
  if (now.diff(storedAt, 'hours').hours > PENDING_TYPED_CODE_MAX_AGE_HOURS) return undefined;
  return code;
}
